import MainLayout from '@/app/layouts/MainLayout';
import { clientConfig } from '@/config/clientConfig';
import { env } from '@/config/env';
import { RequireAbility, RoleEditPage, RoleManagementPage } from '@/domains/access-control';
import {
  AuthCallbackPage,
  ChangePasswordPage,
  ForgotPasswordPage,
  LoginPage,
  ProtectedRoute,
} from '@/domains/auth';
import { SchedulePage } from '@/domains/calendar';
import { HelpPage } from '@/domains/help';
import { JobDetailPage, JobsPage } from '@/domains/jobs';
import { AreasPage, RegionsPage } from '@/domains/locations';
import PlannerPage from '@/domains/planner/pages/PlannerPage';
import {
  AuditTrailPage,
  CategoryTasksPage,
  DisciplinesPage,
  FormBuilderPage,
  FormListPage,
  JobCategoriesPage,
  JobManagementPage,
  JobTitlesPage,
  LeaveTypesPage,
  MobileSsoConfigPage,
  SettingsPage,
  SkillsPage,
  TaskTypesPage,
} from '@/domains/settings';
import { ShiftDetailPage, ShiftsPage } from '@/domains/shifts';
import { TeamDetailPage, TeamsPage } from '@/domains/teams';
import { TestCoveragePage } from '@/domains/test-coverage';
import {
  AddUserPage,
  LeaveCalendarPage,
  NewStartersPage,
  SkillsMatrixPage,
  UserDetailPage,
  UsersPage,
} from '@/domains/users';
import { OfflineBanner } from '@/shared/components';
import { Route, Routes } from 'react-router';
import { LandingRoute } from './LandingRoute';

/**
 * AppRouter - Central route configuration
 *
 * Route structure:
 * - Protected routes: All routes under MainLayout
 * - Catch-all: 404 page
 */
export function AppRouter() {
  return (
    <>
      <OfflineBanner />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Index: land on the first screen the user can access (Dashboard when they
              hold `read Report`, otherwise redirect) — avoids a 403 on login. */}
          <Route index element={<LandingRoute />} />
          <Route
            path="jobs"
            element={
              <RequireAbility action="read" subject="Job">
                <JobsPage />
              </RequireAbility>
            }
          />
          <Route
            path="jobs/:jobId"
            element={
              <RequireAbility action="read" subject="Job">
                <JobDetailPage />
              </RequireAbility>
            }
          />
          <Route
            path="schedule"
            element={
              <RequireAbility action="read" subject="Planner">
                <SchedulePage />
              </RequireAbility>
            }
          />
          {/* Planner Route TODO: test and remove if not needed */}
          <Route
            path="planner"
            element={
              <RequireAbility action="read" subject="Planner">
                <PlannerPage />
              </RequireAbility>
            }
          />
          {/* SC-6657: the Users section is gated on `read User`. Hiding the nav item
              alone would leave the page reachable by typing the URL, so the routes
              carry the same guard the API now enforces. */}
          <Route
            path="users"
            element={
              <RequireAbility action="read" subject="User">
                <UsersPage />
              </RequireAbility>
            }
          />
          <Route
            path="users/add"
            element={
              <RequireAbility action="create" subject="User">
                <AddUserPage />
              </RequireAbility>
            }
          />
          {/* SC-6617: HR surfaces are mounted only for brands that manage this
              data in Works Lite rather than their own HR system. */}
          {clientConfig.features.skills && (
            <Route
              path="users/skills-matrix"
              element={
                <RequireAbility action="read" subject="User">
                  <SkillsMatrixPage />
                </RequireAbility>
              }
            />
          )}
          <Route
            path="users/new-starters"
            element={
              <RequireAbility action="read" subject="User">
                <NewStartersPage />
              </RequireAbility>
            }
          />
          {clientConfig.features.leaveTypes && (
            <Route
              path="users/leave-calendar"
              element={
                <RequireAbility action="read" subject="User">
                  <LeaveCalendarPage />
                </RequireAbility>
              }
            />
          )}
          <Route
            path="users/:userId"
            element={
              <RequireAbility action="read" subject="User">
                <UserDetailPage />
              </RequireAbility>
            }
          />
          <Route
            path="teams"
            element={
              <RequireAbility action="read" subject="Team">
                <TeamsPage />
              </RequireAbility>
            }
          />
          <Route
            path="teams/:teamId"
            element={
              <RequireAbility action="read" subject="Team">
                <TeamDetailPage />
              </RequireAbility>
            }
          />
          <Route
            path="shifts"
            element={
              <RequireAbility action="read" subject="Shift">
                <ShiftsPage />
              </RequireAbility>
            }
          />
          <Route
            path="shifts/:shiftId"
            element={
              <RequireAbility action="read" subject="Shift">
                <ShiftDetailPage />
              </RequireAbility>
            }
          />
          {/* Settings opens for `read Settings` OR `read AuditTrail` — an audit-only role
              reaches the page, which filters its cards down to just Audit Trail. */}
          <Route
            path="settings"
            element={
              <RequireAbility
                action="read"
                subject="Settings"
                anyOf={[{ action: 'read', subject: 'AuditTrail' }]}
              >
                <SettingsPage />
              </RequireAbility>
            }
          />
          <Route
            path="settings/job-management"
            element={
              <RequireAbility action="read" subject="Settings">
                <JobManagementPage />
              </RequireAbility>
            }
          />
          <Route
            path="settings/job-categories"
            element={
              <RequireAbility action="read" subject="JobCategory">
                <JobCategoriesPage />
              </RequireAbility>
            }
          />
          <Route
            path="settings/task-types"
            element={
              <RequireAbility action="read" subject="TaskType">
                <TaskTypesPage />
              </RequireAbility>
            }
          />
          <Route
            path="settings/category-tasks"
            element={
              <RequireAbility action="read" subject="JobCategory">
                <CategoryTasksPage />
              </RequireAbility>
            }
          />
          <Route
            path="settings/form-builder"
            element={
              <RequireAbility action="read" subject="Form">
                <FormListPage />
              </RequireAbility>
            }
          />
          {/* A read Form role may open the builder to view a form; the Save affordance itself
              is gated on create/update inside FormBuilder (WRK-5669). */}
          <Route
            path="settings/form-builder/:id"
            element={
              <RequireAbility action="read" subject="Form">
                <FormBuilderPage />
              </RequireAbility>
            }
          />
          <Route
            path="settings/job-titles"
            element={
              <RequireAbility action="read" subject="JobTitle">
                <JobTitlesPage />
              </RequireAbility>
            }
          />
          {clientConfig.features.disciplines && (
            <Route
              path="settings/disciplines"
              element={
                <RequireAbility action="read" subject="Discipline">
                  <DisciplinesPage />
                </RequireAbility>
              }
            />
          )}
          {clientConfig.features.skills && (
            <Route
              path="settings/skills"
              element={
                <RequireAbility action="read" subject="Skill">
                  <SkillsPage />
                </RequireAbility>
              }
            />
          )}
          {clientConfig.features.leaveTypes && (
            <Route
              path="settings/leave-types"
              element={
                <RequireAbility action="read" subject="LeaveType">
                  <LeaveTypesPage />
                </RequireAbility>
              }
            />
          )}
          {/* Regions module (SC-6259) — reinstated per WRK-5619, mounted only
              for brands that opt in via clientConfig.features.regions. */}
          {clientConfig.features.regions && (
            <Route
              path="settings/regions"
              element={
                <RequireAbility action="read" subject="Settings">
                  <RegionsPage />
                </RequireAbility>
              }
            />
          )}
          <Route
            path="settings/areas"
            element={
              <RequireAbility action="read" subject="Area">
                <AreasPage />
              </RequireAbility>
            }
          />
          <Route
            path="settings/audit-trail"
            element={
              <RequireAbility action="read" subject="AuditTrail">
                <AuditTrailPage />
              </RequireAbility>
            }
          />
          <Route
            path="settings/mobile-sso"
            element={
              <RequireAbility action="read" subject="AccessControl">
                <MobileSsoConfigPage />
              </RequireAbility>
            }
          />
          <Route
            path="settings/role-management"
            element={
              <RequireAbility action="read" subject="AccessControl">
                <RoleManagementPage />
              </RequireAbility>
            }
          />
          <Route
            path="settings/role-management/new"
            element={
              <RequireAbility action="create" subject="AccessControl">
                <RoleEditPage />
              </RequireAbility>
            }
          />
          <Route
            path="settings/role-management/:roleId"
            element={
              <RequireAbility action="update" subject="AccessControl">
                <RoleEditPage />
              </RequireAbility>
            }
          />
          <Route path="help" element={<HelpPage />} />
          {(env.isDevelopment || env.isStaging) && (
            <Route path="test-coverage" element={<TestCoveragePage />} />
          )}
        </Route>
      </Routes>
    </>
  );
}
